import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { inviteService } from '@/services/invite.service';
import { toast } from 'sonner';

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signup } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [inviteDetails, setInviteDetails] = useState(null);

  // Load invite details
  useEffect(() => {
    const loadInvite = async () => {
      if (inviteToken) {
        try {
          const invite = await inviteService.getInviteByToken(inviteToken);
          setInviteDetails(invite);
          setEmail(invite.email);
        } catch (err) {
          console.error('Failed to load invite:', err);
        }
      }
    };
    loadInvite();
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signup(name, email, password);
      if (inviteToken) {
        localStorage.setItem('pendingInviteToken', inviteToken);
      }
      if (result?.requires_verification) {
        toast.success('Verification code sent to your email');
        return;
      }
      toast.success('Account created successfully!');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center gradient-soft p-4">
      <Card className="w-full max-w-md p-8 shadow-strong">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">Shaadi Buddy</h1>
          <p className="text-muted-foreground">
            {inviteDetails 
              ? `Join ${inviteDetails.organization_name}`
              : 'Create your account to get started'
            }
          </p>
        </div>

        {inviteDetails && (
          <Alert className="mb-6 border-primary bg-primary/10">
            <AlertDescription className="text-sm">
              You've been invited to join <strong>{inviteDetails.organization_name}</strong> as a{' '}
              <strong>{inviteDetails.role.replace('_', ' ')}</strong>
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert className="mb-6 border-destructive bg-destructive/10">
            <AlertDescription className="text-destructive">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name</Label>
            <div className="relative mt-1.5">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="pl-10"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="pl-10"
                required
                disabled={loading || !!inviteDetails}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10"
                required
                disabled={loading}
                minLength={6}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">At least 6 characters</p>
          </div>

          <Button type="submit" className="w-full gradient-primary" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm">
          <p className="text-muted-foreground">
            Already have an account?{' '}
            <Link 
              to={inviteToken ? `/login?invite=${inviteToken}` : '/login'} 
              className="text-primary hover:underline font-medium"
            >
              Sign in
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
}